import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { Layout } from "@/components/layout";
import {
  useGetMe,
  useGetSettings,
  useListUsers,
  useListEvents,
  useListPullers,
  useCreateEvent,
  useCreateEventsBulk,
  useSearchEventCandidates,
  getSearchEventCandidatesQueryKey,
  useUpdateEvent,
  useUpdateSettings,
  useUpdateUser,
  useListPhoneTypes,
  useUpdatePhoneType,
  useBackfillPhoneTags,
  useListAuditLog,
  getListAuditLogQueryKey,
  useListBankingSources,
  useCreateBankingSource,
  useUpdateBankingSource,
  useListRefreshRuns,
  useListSlackChannels,
  useListSlackUsers,
  useListSlackAlertConfigs,
  useUpdateSlackAlertConfig,
  useAutoMapSlackUsers,
  useSaveSlackMappings,
  useTestSlackAlert,
  useListHoldAlertConfigs,
  useUpdateHoldAlertConfig,
  useTestHoldAlert,
  useGetEventAlertConfig,
  useUpdateEventAlertConfig,
  useTestEventAlert,
  getGetEventAlertConfigQueryKey,
  useBulkSetBuybackFveIntent,
  getListHoldAlertConfigsQueryKey,
  getListRefreshRunsQueryKey,
  getListEventsQueryKey,
  getGetSettingsQueryKey,
  getListUsersQueryKey,
  getListPhoneTypesQueryKey,
  getListBankingSourcesQueryKey,
  getListSlackAlertConfigsQueryKey,
  getListSlackChannelsQueryKey,
  getListSlackUsersQueryKey,
  type Event as ReachEvent,
  type AppUser,
  type Settings as AppSettings,
  type NotationConfig,
  type NotationRule,
  type PhoneTypeConfig,
  type BankingSource,
  type RefreshRun,
  type Puller,
  type SlackChannel,
  type SlackUser,
  type SlackAlertConfig,
  type SlackAutoMapSuggestion,
  type HoldAlertConfig,
  useRepostPullerAlerts,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Plus, CheckCircle2, XCircle, MinusCircle, Clock, Upload, Link2, Zap, RotateCcw, Search, ChevronRight, ChevronDown, AlertTriangle, Megaphone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { extractArtistFromLabel } from "@/lib/event-grouping";
import { DeleteEventDialog } from "@/components/delete-event-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Settings() {
  const { data: me } = useGetMe();
  const { data: settings, isLoading } = useGetSettings();

  if (me?.role !== "admin") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full w-full">
          <p className="text-muted-foreground">Settings are restricted to administrators.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-full w-full bg-background p-2 sm:p-4 overflow-hidden">
        <div className="mb-4 shrink-0">
          <h1 className="text-lg font-medium text-foreground">Settings</h1>
          <p className="text-xs text-muted-foreground">Manage global configurations and system state</p>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : settings ? (
          <div className="flex-1 overflow-hidden flex flex-col bg-card border border-border rounded-md">
            <Tabs defaultValue="events" className="flex-1 min-h-0 flex flex-col w-full">
              <div className="border-b border-border bg-muted p-2 shrink-0">
                <TabsList className="bg-transparent justify-start h-auto w-full flex-wrap gap-2 p-0">
                  <TabsTrigger value="events" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Events</TabsTrigger>
                  <TabsTrigger value="api" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">API & Integrations</TabsTrigger>
                  <TabsTrigger value="mercury" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Mercury</TabsTrigger>
                  <TabsTrigger value="banking" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Banking Sources</TabsTrigger>
                  <TabsTrigger value="phone" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Phone Testing</TabsTrigger>
                  <TabsTrigger value="sales" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Sales Sync</TabsTrigger>
                  <TabsTrigger value="defaults" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Hold Defaults</TabsTrigger>
                  <TabsTrigger value="notation" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Notation</TabsTrigger>
                  <TabsTrigger value="users" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Users</TabsTrigger>
                  <TabsTrigger value="alerts" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Alerts</TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 min-h-0 overflow-auto custom-scrollbar p-2 sm:p-4">
                <TabsContent value="events" className="m-0 h-full">
                  <EventsTab />
                </TabsContent>

                <TabsContent value="api" className="m-0 space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">Integrations</h3>
                    <div className="grid gap-4">
                      {settings.integrations?.map(i => (
                        <div key={i.name} className="flex items-center justify-between p-3 border border-border rounded bg-panel">
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {i.name}
                              {i.configured ? <Badge variant="outline" className="border-green-500/30 text-green-500 bg-green-500/10">CONFIGURED</Badge> : <Badge variant="outline" className="border-amber-500/30 text-amber-500 bg-amber-500/10">MISSING</Badge>}
                            </div>
                            {i.detail && <div className="text-xs text-muted-foreground mt-1">{i.detail}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="mercury" className="m-0 space-y-6">
                  <div className="max-w-xl space-y-4">
                    <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">Mercury Banking Settings</h3>
                    <div className="p-4 border border-border rounded bg-panel space-y-4">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">API Token</span>
                        <span className="font-mono text-xs">{settings.mercury?.apiTokenConfigured ? "Configured" : "Missing"}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Webhook Secret</span>
                        <span className="font-mono text-xs">{settings.mercury?.webhookSecretConfigured ? "Configured" : "Missing"}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Sweep Destination</span>
                        <span className="font-mono text-xs">{settings.mercury?.sweepDestinationLabel || "Not Set"}</span>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="banking" className="m-0 space-y-6">
                  <BankingSourcesTab />
                </TabsContent>

                <TabsContent value="phone" className="m-0 space-y-6">
                  <PhoneTab settings={settings} />
                </TabsContent>

                <TabsContent value="sales" className="m-0 space-y-6">
                  <SalesSyncTab settings={settings} />
                </TabsContent>

                <TabsContent value="defaults" className="m-0 space-y-6">
                  <HoldDefaultsTab settings={settings} />
                </TabsContent>

                <TabsContent value="notation" className="m-0 space-y-6">
                  <NotationTab settings={settings} />
                </TabsContent>

                <TabsContent value="users" className="m-0 space-y-6">
                  <UsersTab />
                </TabsContent>

                <TabsContent value="alerts" className="m-0 space-y-6">
                  <AlertsTab />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}

function EventsTab() {
  const qc = useQueryClient();
  const { data: events, isLoading } = useListEvents();
  const invalidate = () => qc.invalidateQueries({ queryKey: getListEventsQueryKey() });
  const update = useUpdateEvent({
    mutation: { onSuccess: invalidate, onError: () => toast.error("Failed to update event") },
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-stretch gap-3">
        <AddEventsDialog onChanged={invalidate} />
        <AutoEnableConfig />
        <EventAlertConfig />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <GroupedEventsTable
          events={events ?? []}
          onUpdate={(id, data) => update.mutate({ id, data })}
        />
      )}
    </div>
  );
}

// Admin control for the scheduler's auto-enable pass: when on, the background
// refresh turns on (and full-syncs) disabled events with a Reach ID once they
// fall within `daysOut` days of their date. Fetches/writes settings itself so
// EventsTab does not need the settings prop threaded through.
function AutoEnableConfig() {
  const qc = useQueryClient();
  const { data: settings } = useGetSettings();
  const update = useUpdateSettings({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        toast.success("Auto-enable settings saved");
      },
      onError: () => toast.error("Failed to save auto-enable settings"),
    },
  });

  const [enabled, setEnabled] = useState(false);
  const [daysOut, setDaysOut] = useState("7");

  useEffect(() => {
    if (!settings?.autoEnable) return;
    setEnabled(!!settings.autoEnable.enabled);
    setDaysOut(String(settings.autoEnable.daysOut ?? 7));
  }, [settings?.autoEnable?.enabled, settings?.autoEnable?.daysOut]);

  const schedulerOn = !!settings?.refresh?.scheduledRefreshEnabled;

  const save = () => {
    const parsed = parseInt(daysOut, 10);
    const clamped = Math.min(90, Math.max(1, Number.isNaN(parsed) ? 7 : parsed));
    setDaysOut(String(clamped));
    update.mutate({ data: { autoEnable: { enabled, daysOut: clamped } } });
  };

  return (
    <div className="flex flex-1 min-w-[280px] flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-panel px-4 py-2.5">
      <span
        className="text-sm font-medium text-foreground"
        title="Automatically turn on an event — and run a full sync — once it falls within the window below of its event date. Only events with a Reach ID are auto-enabled, and this runs on the shared scheduled background refresh."
      >
        Auto-Enable Events
      </span>
      <Switch checked={enabled} onCheckedChange={setEnabled} />
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-muted-foreground">within</span>
        <Input
          type="number"
          min={1}
          max={90}
          className="h-7 w-16 text-xs"
          value={daysOut}
          onChange={(e) => setDaysOut(e.target.value)}
        />
        <span className="text-[11px] text-muted-foreground">days of event date</span>
      </div>
      {!schedulerOn && (
        <span
          className="text-[11px] text-amber-500"
          title="Scheduled background refresh is off, so auto-enable will not run until it is turned back on (Settings → Hold Defaults)."
        >
          scheduler off
        </span>
      )}
      <Button size="sm" className="ml-auto h-7" onClick={save} disabled={update.isPending}>
        {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
        Save
      </Button>
    </div>
  );
}

// Global event-active Slack alerting. When an event goes active a header is
// posted to this channel and buy-back/sell intents reply in-thread @mentioning
// the puller. Config is a single puller-independent row; posts to a channel.
function EventAlertConfig() {
  const qc = useQueryClient();
  const cfg = useGetEventAlertConfig();
  const channels = useListSlackChannels();

  const update = useUpdateEventAlertConfig({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetEventAlertConfigQueryKey() });
      },
      onError: () => toast.error("Failed to update event alerts"),
    },
  });

  const test = useTestEventAlert({
    mutation: {
      onSuccess: (res) => {
        if (res.ok) toast.success("Test alert sent");
        else
          toast.error(
            res.error ? `Test failed: ${res.error}` : "Test alert failed",
          );
      },
      onError: () => toast.error("Failed to send test alert"),
    },
  });

  const data = cfg.data;
  const channelList = channels.data ?? [];

  return (
    <div className="flex flex-1 min-w-[280px] flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-panel px-4 py-2.5">
      <span
        className="text-sm font-medium text-foreground"
        title="When an event goes active, post a header to this Slack channel. Buy-back and sell intents reply in-thread @mentioning the puller, with a loud warning on SELL. Fires best-effort — Slack problems never block the app."
      >
        Event Alerts
      </span>
      <Switch
        checked={data?.enabled ?? false}
        disabled={cfg.isLoading || update.isPending}
        onCheckedChange={(v) => update.mutate({ data: { enabled: v } })}
      />
      <SlackTargetCombobox
        targetType="channel"
        targetId={data?.targetId ?? null}
        channels={channelList}
        users={[]}
        onChange={(_targetType, targetId) =>
          update.mutate({ data: { targetType: "channel", targetId } })
        }
      />
      <Button
        size="sm"
        variant="outline"
        className="ml-auto h-7 text-xs rounded-full"
        disabled={test.isPending || !data?.enabled || !data?.targetId}
        onClick={() => test.mutate()}
      >
        {test.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
        ) : (
          <Zap className="h-3.5 w-3.5 mr-1 text-amber-400" />
        )}
        Test Fire
      </Button>
    </div>
  );
}

// Primary import flow: search OUR Reach holdings (the events we actually hold
// tickets for) by artist, then bulk-import the selected events. Each holdings
// row carries a real Reach ID, the artist, the auto-derived market, and FVE
// eligibility (unsold count + buy-back). Imported events always land disabled.
// The Skybox catalog stays as a secondary fallback for events not yet in our
// holdings; those rows carry no Reach ID (importing still allowed, but Reach
// sync is skipped until the operator optionally types one in here).
function EventSearchImport({ onImported }: { onImported: () => void }) {
  const [input, setInput] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [reachIds, setReachIds] = useState<Record<string, string>>({});

  const { data: candidates, isFetching } = useSearchEventCandidates(
    { q: submittedQuery },
    {
      query: {
        enabled: submittedQuery.trim().length > 0,
        queryKey: getSearchEventCandidatesQueryKey({ q: submittedQuery }),
      },
    },
  );

  const bulkCreate = useCreateEventsBulk({
    mutation: {
      onSuccess: (created) => {
        onImported();
        setSelected({});
        setReachIds({});
        toast.success(
          created.length === 1
            ? "1 event imported (disabled)"
            : `${created.length} events imported (disabled)`,
        );
      },
      onError: () => toast.error("Failed to import events"),
    },
  });

  const runSearch = () => {
    const q = input.trim();
    if (!q) {
      toast.error("Enter an artist or keyword to search");
      return;
    }
    setSelected({});
    setReachIds({});
    setSubmittedQuery(q);
  };

  const rows = candidates ?? [];

  // The Reach ID to import a row with: its own (Reach-sourced) or one optionally
  // typed in by the operator (Skybox-sourced). Empty string means "no Reach ID"
  // — the event still imports, just without Reach inventory sync.
  const resolvedReachId = (c: (typeof rows)[number]): string =>
    (c.reachEventId ?? reachIds[c.id] ?? "").trim();

  // Any checked row is importable now — a Reach ID is no longer required.
  const importable = rows.filter((c) => selected[c.id]);

  const doImport = () => {
    if (importable.length === 0) {
      toast.error("Select at least one event to import");
      return;
    }
    bulkCreate.mutate({
      data: {
        events: importable.map((c) => {
          const reachId = resolvedReachId(c);
          return {
            reachEventId: reachId || null,
            label: c.name?.trim() || c.performer?.trim() || reachId || "Untitled event",
            eventDate: c.date?.trim() || undefined,
            // Persist the artist + auto-derived market so the imported event
            // lands under the right artist (never "OTHER — NO ARTIST").
            performer: c.performer?.trim() || null,
            ...(c.marketType ? { marketType: c.marketType } : {}),
          };
        }),
      },
    });
  };

  return (
    <div className="border border-border rounded bg-panel p-3 space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Import Events</h3>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1 flex-1 min-w-[220px]">
          <Label className="text-[11px] text-muted-foreground">Artist or keyword</Label>
          <Input
            className="h-8 text-xs"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            placeholder="e.g. Harry Styles"
          />
        </div>
        <Button size="sm" className="h-8" onClick={runSearch} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          <span className="ml-1">Search</span>
        </Button>
      </div>

      {submittedQuery && isFetching && rows.length === 0 && (
        <p className="text-[11px] text-muted-foreground py-2 flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          Searching for “{submittedQuery}”…
        </p>
      )}

      {submittedQuery && !isFetching && rows.length === 0 && (
        <p className="text-[11px] text-muted-foreground py-2">No upcoming events found for “{submittedQuery}”.</p>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          <div className="border border-border rounded max-h-80 overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10 bg-panel">
                <tr className="border-b border-border">
                  <th className="w-8 px-2 py-1.5 text-left font-medium text-muted-foreground"></th>
                  <th className="px-2 py-1.5 text-left font-medium text-muted-foreground min-w-[160px]">Event</th>
                  <th className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap">Date</th>
                  <th className="px-2 py-1.5 text-left font-medium text-muted-foreground min-w-[120px]">Venue · City</th>
                  <th className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap">Market</th>
                  <th className="px-2 py-1.5 text-right font-medium text-muted-foreground whitespace-nowrap">Active</th>
                  <th className="px-2 py-1.5 text-right font-medium text-muted-foreground whitespace-nowrap">Sold+Unf.</th>
                  <th className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap">Reach ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((c) => {
                  const checked = !!selected[c.id];
                  const needsReachId = !c.reachEventId;
                  const isHolding = c.source === "holdings";
                  const fmtDate = (() => {
                    if (!c.date) return "—";
                    const m = c.date.match(/(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
                    if (!m) return c.date;
                    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                    const mo = months[Number(m[2]) - 1];
                    const d = Number(m[3]);
                    const yr = m[1];
                    if (!m[4]) return `${mo} ${d}, ${yr}`;
                    let h = Number(m[4]);
                    const min = m[5];
                    const ampm = h >= 12 ? "pm" : "am";
                    if (h > 12) h -= 12;
                    if (h === 0) h = 12;
                    return `${mo} ${d}, ${yr} ${h}:${min} ${ampm}`;
                  })();
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => setSelected((s) => ({ ...s, [c.id]: !s[c.id] }))}
                    >
                      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) =>
                            setSelected((s) => ({ ...s, [c.id]: !!v }))
                          }
                        />
                      </td>
                      <td className="px-2 py-1.5 max-w-[220px]">
                        <div className="font-medium text-foreground truncate">
                          {c.performer?.trim() || c.name || "(no name)"}
                        </div>
                        {c.name && c.performer?.trim() && (
                          <div className="text-[11px] text-muted-foreground truncate">{c.name}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">{fmtDate}</td>
                      <td className="px-2 py-1.5 max-w-[160px]">
                        <div className="truncate text-muted-foreground">
                          {[c.venue, c.city].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {c.marketType ? (
                          <Badge
                            variant="outline"
                            className="text-[10px]"
                            title={`Market auto-set from venue country${c.country ? ` (${c.country})` : ""}`}
                          >
                            {c.marketType === "domestic" ? "Domestic" : "International"}
                          </Badge>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {isHolding ? (
                          <span title="Unsold inventory still listed or delisted">{c.unsoldCount ?? 0}</span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {isHolding ? (
                          <span
                            className={c.buybackCandidate ? "text-amber-500" : "text-muted-foreground"}
                            title="Sold but not yet delivered — recoverable for buy-back"
                          >
                            {c.buybackCount ?? 0}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        {needsReachId ? (
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-[10px] shrink-0">Skybox</Badge>
                            <Input
                              className="h-6 w-28 font-mono text-xs"
                              value={reachIds[c.id] ?? ""}
                              onChange={(e) =>
                                setReachIds((m) => ({ ...m, [c.id]: e.target.value }))
                              }
                              placeholder="Reach ID (optional)"
                            />
                          </div>
                        ) : (
                          <span className="font-mono text-[11px] text-muted-foreground">{c.reachEventId}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-muted-foreground">
              {importable.length} selected, ready to import
              {" · imported events start disabled"}
            </p>
            <Button size="sm" className="h-8" onClick={doImport} disabled={bulkCreate.isPending || importable.length === 0}>
              {bulkCreate.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              <span className="ml-1">Import Selected</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Secondary fallback: the original single-event manual form, collapsed by
// default so the search-driven import is the primary path.
function ManualAddEvent({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reachEventId: "", label: "", eventDate: "" });
  const create = useCreateEvent({
    mutation: {
      onSuccess: () => {
        onAdded();
        setForm({ reachEventId: "", label: "", eventDate: "" });
        toast.success("Event added");
      },
      onError: () => toast.error("Failed to add event"),
    },
  });

  const submit = () => {
    if (!form.reachEventId.trim() || !form.label.trim()) {
      toast.error("Reach event ID and label are required");
      return;
    }
    create.mutate({
      data: {
        reachEventId: form.reachEventId.trim(),
        label: form.label.trim(),
        eventDate: form.eventDate.trim() || undefined,
      },
    });
  };

  return (
    <div className="border border-border rounded bg-panel">
      <button
        type="button"
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        onClick={() => setOpen((v) => !v)}
      >
        <span>Add manually</span>
        <ChevronsUpDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="border-t border-border p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Reach Event ID</Label>
              <Input className="h-8 w-40 font-mono text-xs" value={form.reachEventId} onChange={(e) => setForm({ ...form, reachEventId: e.target.value })} placeholder="123456" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Label</Label>
              <Input className="h-8 w-56 text-xs" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Harry Styles — Wembley" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Event Date</Label>
              <Input className="h-8 w-36 text-xs" value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} placeholder="2026-06-12" />
            </div>
            <Button size="sm" className="h-8" onClick={submit} disabled={create.isPending}>
              {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              <span className="ml-1">Add</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Wraps both event-creation flows (search-driven import + manual single add) in
// a single modal so the Events tab header stays slim: the tab shows only a
// prominent "Add Events" button beside the Auto-Enable row, and both flows live
// exclusively inside this dialog.
function AddEventsDialog({ onChanged }: { onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-auto self-stretch gap-1.5 px-5 text-sm font-medium">
          <Plus className="h-4 w-4" />
          Add Events
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <DialogTitle>Add Events</DialogTitle>
          <DialogDescription>
            Search your Reach holdings to import events, or add one manually by
            Reach ID. Imported events start disabled.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <EventSearchImport onImported={onChanged} />
          <ManualAddEvent onAdded={onChanged} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Events table grouped by artist with collapsible group headers. Groups are
// sorted alphabetically (the "Other" no-performer bucket last); within a group,
// events sort by date ascending.
type EventUpdatePayload = {
  label?: string;
  eventDate?: string;
  reachEventId?: string | null;
  performer?: string | null;
  enabled?: boolean;
  sortOrder?: number;
  marketType?: "international" | "domestic";
  buybacksEnabled?: boolean;
};

function GroupedEventsTable({
  events,
  onUpdate,
}: {
  events: ReachEvent[];
  onUpdate: (id: ReachEvent["id"], data: EventUpdatePayload) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showPast, setShowPast] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const groups = useMemo(() => {
    const OTHER = "Other";
    // Key buckets case-insensitively so a performer backfilled as "HARRY STYLES"
    // groups with an extracted "Harry Styles"; keep the first-seen casing for the
    // displayed header.
    const map = new Map<string, { artist: string; events: ReachEvent[] }>();
    for (const e of events) {
      const performer = e.performer?.trim();
      // Fall back to the parsed artist, then the whole label, and only treat an
      // event as "no artist" when even the label is blank. An event literally
      // labelled "Noah Kahan" must group under "Noah Kahan", never "Other" —
      // matching the shared artistKey() helper in lib/event-grouping.
      const display = performer
        ? performer
        : (extractArtistFromLabel(e.label, e.venue) ?? e.label.trim()) || OTHER;
      const key = display.toLowerCase();
      const bucket = map.get(key);
      if (bucket) bucket.events.push(e);
      else map.set(key, { artist: display, events: [e] });
    }
    const dayOf = (e: ReachEvent) => e.eventDate?.match(/\d{4}-\d{2}-\d{2}/)?.[0] ?? "9999-99-99";
    const entries = [...map.values()].map(({ artist, events: evts }) => ({
      artist,
      events: [...evts].sort((a, b) => dayOf(a).localeCompare(dayOf(b))),
    }));
    entries.sort((a, b) => {
      if (a.artist === OTHER) return 1;
      if (b.artist === OTHER) return -1;
      return a.artist.localeCompare(b.artist);
    });
    return entries;
  }, [events]);

  // Time-window filter: by default only show events happening within the next
  // week. Past events and events more than a week out are hidden behind the
  // toggles above the table. Undated events can't be time-classified, so they
  // always stay visible (they usually need attention anyway).
  const { visibleGroups, pastCount, farCount } = useMemo(() => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const ymd = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const now = new Date();
    const todayStr = ymd(now);
    const weekAhead = new Date(now);
    weekAhead.setDate(weekAhead.getDate() + 7);
    const weekStr = ymd(weekAhead);

    let past = 0;
    let far = 0;
    const visible: { artist: string; events: ReachEvent[] }[] = [];
    for (const g of groups) {
      const evts = g.events.filter((e) => {
        const day = e.eventDate?.match(/\d{4}-\d{2}-\d{2}/)?.[0];
        if (!day) return true; // undated — can't classify, keep visible
        if (day < todayStr) {
          past++;
          return showPast;
        }
        if (day > weekStr) {
          far++;
          return showUpcoming;
        }
        return true; // within the next week
      });
      // Drop groups whose events are all filtered out.
      if (evts.length > 0) visible.push({ artist: g.artist, events: evts });
    }
    return { visibleGroups: visible, pastCount: past, farCount: far };
  }, [groups, showPast, showUpcoming]);

  if (events.length === 0) {
    return (
      <div className="border border-border rounded py-6 text-center text-xs text-muted-foreground">
        No events configured.
      </div>
    );
  }

  const otherCount =
    visibleGroups.find((g) => g.artist === "Other")?.events.length ?? 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]">
        <label className="flex cursor-pointer select-none items-center gap-1.5">
          <Checkbox checked={showPast} onCheckedChange={(v) => setShowPast(v === true)} />
          <span className="text-muted-foreground">
            Show past events{!showPast && pastCount > 0 ? ` (${pastCount})` : ""}
          </span>
        </label>
        <label className="flex cursor-pointer select-none items-center gap-1.5">
          <Checkbox checked={showUpcoming} onCheckedChange={(v) => setShowUpcoming(v === true)} />
          <span className="text-muted-foreground">
            Show upcoming &gt; 1 week away{!showUpcoming && farCount > 0 ? ` (${farCount})` : ""}
          </span>
        </label>
      </div>
      {otherCount > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            {otherCount} event{otherCount === 1 ? "" : "s"} still
            {" "}
            <span className="font-semibold">without an artist</span> — Reach has
            no performer for them. They sit in the “Other” group below; type an
            artist name in the Artist column to fix the grouping. A manual artist
            is preserved across refreshes.
          </span>
        </div>
      )}
      <div className="border border-border rounded overflow-x-auto custom-scrollbar">
        <table className="w-full text-xs min-w-[680px]">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left py-2 px-3 font-medium" title="Enabled — turn the event on/off">Active</th>
              <th className="text-left py-2 px-3 font-medium" title="Buy-Backs enabled">Buybacks?</th>
              <th className="text-left py-2 px-3 font-medium">Label</th>
              <th className="text-left py-2 px-3 font-medium">Artist</th>
              <th className="text-left py-2 px-3 font-medium">Reach ID</th>
              <th className="text-left py-2 px-3 font-medium">Date</th>
              <th className="text-left py-2 px-3 font-medium">Market</th>
              <th className="text-right py-2 px-3 font-medium">Remaining</th>
              <th className="text-right py-2 px-3 font-medium">Held</th>
              <th className="text-left py-2 px-3 font-medium">Status</th>
              <th className="text-right py-2 px-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.length === 0 ? (
              <tr>
                <td colSpan={11} className="py-6 text-center text-xs text-muted-foreground">
                  No events happening within the next week.
                  {pastCount + farCount > 0
                    ? " Use the toggles above to reveal past or upcoming events."
                    : ""}
                </td>
              </tr>
            ) : (
              visibleGroups.map((group) => {
              const isCollapsed = !!collapsed[group.artist];
              const isOther = group.artist === "Other";
              return (
                <Fragment key={group.artist}>
                  <tr
                    className={cn(
                      "sticky top-0 z-10 backdrop-blur",
                      isOther ? "bg-amber-500/15" : "bg-muted/80",
                    )}
                  >
                    <td colSpan={11} className="px-3 py-1.5">
                      <button
                        type="button"
                        className={cn(
                          "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider",
                          isOther
                            ? "text-amber-700 dark:text-amber-300"
                            : "text-foreground",
                        )}
                        onClick={() =>
                          setCollapsed((c) => ({ ...c, [group.artist]: !c[group.artist] }))
                        }
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                        {isOther && <AlertTriangle className="h-3.5 w-3.5" />}
                        {isOther ? "Other — no artist" : group.artist}
                        <span className="text-muted-foreground font-normal normal-case">({group.events.length})</span>
                      </button>
                    </td>
                  </tr>
                  {!isCollapsed &&
                    group.events.map((ev) => (
                      <EventRow
                        key={ev.id}
                        ev={ev}
                        needsArtist={isOther}
                        onUpdate={(data) => onUpdate(ev.id, data)}
                      />
                    ))}
                </Fragment>
              );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type EditingField = "label" | "artist" | "reachEventId" | "eventDate" | "marketType" | null;

function StatusDots({
  enabled,
  buybacksEnabled,
  onToggleEnabled,
  onToggleBuybacks,
}: {
  enabled: boolean;
  buybacksEnabled: boolean;
  onToggleEnabled: () => void;
  onToggleBuybacks: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        title={enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
        onClick={onToggleEnabled}
        className={cn(
          "h-2.5 w-2.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          enabled
            ? "bg-green-500 border-green-500 hover:bg-green-600"
            : "bg-transparent border-border hover:border-muted-foreground",
        )}
      />
      <button
        type="button"
        title={buybacksEnabled ? "Buy-Backs on — click to disable" : "Buy-Backs off — click to enable"}
        onClick={onToggleBuybacks}
        className={cn(
          "h-2.5 w-2.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          buybacksEnabled
            ? "bg-blue-500 border-blue-500 hover:bg-blue-600"
            : "bg-transparent border-border hover:border-muted-foreground",
        )}
      />
    </div>
  );
}

function EditableCell({
  value,
  placeholder,
  className,
  inputClassName,
  isEditing,
  onActivate,
  onSave,
  onCancel,
  inputProps,
}: {
  value: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  isEditing: boolean;
  onActivate: () => void;
  onSave: (val: string) => void;
  onCancel: () => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(value);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isEditing, value]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        className={cn(
          "h-7 text-xs px-1.5 rounded border border-ring bg-background outline-none w-full",
          inputClassName,
        )}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onSave(draft)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.currentTarget.blur(); }
          if (e.key === "Escape") { onCancel(); }
        }}
        {...inputProps}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      title="Click to edit"
      onClick={onActivate}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onActivate(); }}
      className={cn(
        "cursor-text select-none rounded px-1 -mx-1 hover:bg-muted transition-colors min-w-[2rem] inline-block",
        !value && "text-muted-foreground italic",
        className,
      )}
    >
      {value || placeholder || "—"}
    </span>
  );
}

function RepostAlertsButton({ eventId }: { eventId: number }) {
  const repost = useRepostPullerAlerts({
    mutation: {
      onSuccess(data) {
        if (data.messagesTotal === 0) {
          toast.info("No posted puller alerts to update for this event.");
          return;
        }
        const parts = [
          `Updated ${data.messagesUpdated} alert${data.messagesUpdated === 1 ? "" : "s"}`,
        ];
        if (data.relink?.matched) {
          parts.push(
            `linked ${data.relink.matched} listing${data.relink.matched === 1 ? "" : "s"}`,
          );
        }
        if (data.messagesSkippedUnknown) {
          parts.push(`${data.messagesSkippedUnknown} still unknown`);
        }
        if (data.messagesFailed) parts.push(`${data.messagesFailed} failed`);
        toast.success(parts.join(", "));
      },
      onError() {
        toast.error("Failed to re-send puller alerts.");
      },
    },
  });
  return (
    <button
      type="button"
      title="Re-send correct pullers into this event's already-posted Slack alerts"
      aria-label="Re-send puller alerts"
      onClick={() => repost.mutate({ id: eventId })}
      disabled={repost.isPending}
      className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
    >
      {repost.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Megaphone className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function EventRow({ ev, onUpdate, needsArtist = false }: { ev: ReachEvent; onUpdate: (data: EventUpdatePayload) => void; needsArtist?: boolean }) {
  const qc = useQueryClient();
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [bbDialogOpen, setBbDialogOpen] = useState(false);
  const missingReachId = !ev.reachEventId;
  const inferredArtist =
    extractArtistFromLabel(ev.label, ev.venue) ?? ev.label.trim();

  const updateEvent = useUpdateEvent({
    mutation: {
      onSuccess: () => qc.invalidateQueries({ queryKey: getListEventsQueryKey() }),
      onError: () => toast.error("Failed to update event"),
    },
  });
  const { mutate: bulkIntent } = useBulkSetBuybackFveIntent();

  function activate(field: EditingField) { setEditingField(field); }
  function cancel() { setEditingField(null); }

  function handleBbToggle() {
    if (ev.buybacksEnabled) {
      onUpdate({ buybacksEnabled: false });
    } else {
      setBbDialogOpen(true);
    }
  }

  function enableBbOnly() {
    onUpdate({ buybacksEnabled: true });
    setBbDialogOpen(false);
  }

  function enableBbAndStamp() {
    setBbDialogOpen(false);
    updateEvent.mutate(
      { id: ev.id, data: { buybacksEnabled: true } },
      {
        onSuccess() {
          bulkIntent(
            { id: ev.id, data: { intent: "buy_back", ids: [] } },
            {
              onSuccess(data) {
                const n = data.updated;
                toast.success(
                  n === 0
                    ? "No eligible listings to update."
                    : `${n} listing${n === 1 ? "" : "s"} set to Buy Back.`,
                );
              },
              onError() {
                toast.error("Failed to stamp listings. Please try again.");
              },
            },
          );
        },
        onError() {
          toast.error("Failed to enable Buy Backs; listings were not updated.");
        },
      },
    );
  }

  function saveLabel(val: string) {
    const next = val.trim();
    if (next && next !== ev.label) onUpdate({ label: next });
    setEditingField(null);
  }
  function saveArtist(val: string) {
    const next = val.trim();
    if (next !== (ev.performer ?? "").trim()) onUpdate({ performer: next });
    setEditingField(null);
  }
  function saveReachId(val: string) {
    const next = val.trim();
    if (next !== (ev.reachEventId ?? "").trim()) onUpdate({ reachEventId: next });
    setEditingField(null);
  }
  function saveDate(val: string) {
    if (val !== (ev.eventDate ?? "")) onUpdate({ eventDate: val });
    setEditingField(null);
  }

  return (
    <>
    <tr className="border-t border-border hover:bg-muted/40">
      <td className="py-1.5 px-3">
        <Checkbox
          checked={ev.enabled}
          onCheckedChange={(v) => onUpdate({ enabled: v === true })}
          title={ev.enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
          aria-label="Enabled"
        />
      </td>
      <td className="py-1.5 px-3">
        <button
          type="button"
          title={(ev.buybacksEnabled ?? false) ? "Buy-Backs on — click to disable" : "Buy-Backs off — click to enable"}
          aria-label="Buy-Backs"
          onClick={handleBbToggle}
          className={cn(
            "inline-flex items-center justify-center rounded p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            (ev.buybacksEnabled ?? false)
              ? "text-amber-400 hover:text-amber-500"
              : "text-muted-foreground/40 hover:text-muted-foreground",
          )}
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </td>
      <td className="py-1.5 px-3 max-w-[13rem]">
        <EditableCell
          value={ev.label}
          isEditing={editingField === "label"}
          onActivate={() => activate("label")}
          onSave={saveLabel}
          onCancel={cancel}
          inputClassName="w-52"
        />
      </td>
      <td className="py-1.5 px-3 max-w-[10rem]">
        <EditableCell
          value={ev.performer ?? ""}
          placeholder={needsArtist ? "Set artist…" : inferredArtist || "—"}
          isEditing={editingField === "artist"}
          onActivate={() => activate("artist")}
          onSave={saveArtist}
          onCancel={cancel}
          inputClassName={cn("w-40", needsArtist && "border-amber-500/60")}
          className={needsArtist && !ev.performer ? "text-amber-600/80 dark:text-amber-400/80" : undefined}
        />
      </td>
      <td className="py-1.5 px-3">
        <EditableCell
          value={ev.reachEventId ?? ""}
          placeholder={missingReachId ? "Add Reach ID…" : "—"}
          isEditing={editingField === "reachEventId"}
          onActivate={() => activate("reachEventId")}
          onSave={saveReachId}
          onCancel={cancel}
          inputClassName={cn("w-28 font-mono", missingReachId && "border-amber-500/60")}
          className={cn("font-mono", missingReachId && !ev.reachEventId ? "text-amber-600/80 dark:text-amber-400/80" : undefined)}
        />
      </td>
      <td className="py-1.5 px-3">
        <EditableCell
          value={ev.eventDate ?? ""}
          placeholder="—"
          isEditing={editingField === "eventDate"}
          onActivate={() => activate("eventDate")}
          onSave={saveDate}
          onCancel={cancel}
          inputClassName="w-32"
        />
      </td>
      <td className="py-1.5 px-3">
        {editingField === "marketType" ? (
          <Select
            value={ev.marketType}
            open
            onOpenChange={(open) => { if (!open) setEditingField(null); }}
            onValueChange={(v) => {
              onUpdate({ marketType: v as "international" | "domestic" });
              setEditingField(null);
            }}
          >
            <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="international">International</SelectItem>
              <SelectItem value="domestic">Domestic</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span
            role="button"
            tabIndex={0}
            title="Click to change market"
            onClick={() => activate("marketType")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") activate("marketType"); }}
            className="cursor-pointer select-none rounded px-1 -mx-1 hover:bg-muted transition-colors capitalize inline-block"
          >
            {ev.marketType ?? "—"}
          </span>
        )}
      </td>
      <td className="py-1.5 px-3 text-right font-mono">{ev.remainingTicketCount}</td>
      <td className="py-1.5 px-3 text-right font-mono">{ev.heldCount}</td>
      <td className="py-1.5 px-3">
        {ev.lastError ? (
          <span className="text-red-400" title={ev.lastError}>Error</span>
        ) : (
          <span className="text-muted-foreground">{ev.refreshStatus || (ev.lastSuccessAt ? "OK" : "—")}</span>
        )}
      </td>
      <td className="py-1.5 px-3 text-right">
        <div className="inline-flex items-center gap-1">
          <RepostAlertsButton eventId={ev.id} />
          <DeleteEventDialog eventId={ev.id} eventLabel={ev.label} />
        </div>
      </td>
    </tr>
    <AlertDialog open={bbDialogOpen} onOpenChange={setBbDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Enable Buy Backs for {ev.label}?</AlertDialogTitle>
          <AlertDialogDescription>
            Would you also like to set all currently Eligible listings for this event to "Buy Back" intent?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={enableBbOnly}>No, just enable</AlertDialogCancel>
          <AlertDialogAction onClick={enableBbAndStamp}>Yes, stamp them</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function SalesSyncTab({ settings }: { settings: AppSettings }) {
  const qc = useQueryClient();
  const update = useUpdateSettings({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        toast.success("Sales sync settings saved");
      },
      onError: () => toast.error("Failed to save sales sync settings"),
    },
  });

  const [pollFrom, setPollFrom] = useState(settings.salesPollFrom ?? "2026-06-01");
  const [syncInterval, setSyncInterval] = useState(
    String(settings.refresh.salesSyncIntervalMinutes ?? 20),
  );
  const [schedulerEnabled, setSchedulerEnabled] = useState(
    !!settings.refresh.scheduledRefreshEnabled,
  );

  const save = () => {
    if (!pollFrom.trim()) {
      toast.error("Poll-from date is required");
      return;
    }
    const parsed = parseInt(syncInterval);
    const minutes = Math.max(10, Number.isNaN(parsed) ? 20 : parsed);
    setSyncInterval(String(minutes));
    update.mutate({
      data: {
        salesPollFrom: pollFrom.trim(),
        refresh: {
          salesSyncIntervalMinutes: minutes,
          scheduledRefreshEnabled: schedulerEnabled,
        },
      },
    });
  };

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">Sales Sync</h3>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-[11px] text-muted-foreground">
              Scheduled background sync
            </Label>
            <p className="text-[11px] text-muted-foreground">
              When on, the app polls BigQuery on the interval below. This is the
              same scheduled-refresh switch that drives the Reach and AMP
              refreshes, so turning it off pauses all background syncing.
            </p>
          </div>
          <Switch
            checked={schedulerEnabled}
            onCheckedChange={setSchedulerEnabled}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] text-muted-foreground">Default poll-from date</Label>
          <Input
            type="date"
            className="h-8 text-xs w-44"
            value={pollFrom}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setPollFrom(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            The earliest date the background sales sync pulls Ticketmaster emails
            from. Operators can still set a one-off override on the Sales page for
            a single manual run.
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] text-muted-foreground">Sync interval (minutes)</Label>
          <Input
            type="number"
            min={10}
            className="h-8 text-xs w-44"
            value={syncInterval}
            onChange={(e) => setSyncInterval(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            How often the background sync checks BigQuery for new Ticketmaster
            sale and listing emails. Minimum 10 minutes to keep BigQuery costs
            down — lower values are clamped to 10. Takes effect on the next cycle
            without a restart, and only runs while scheduled background refresh is
            enabled.
          </p>
        </div>
      </div>

      <Button size="sm" onClick={save} disabled={update.isPending}>
        {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
        Save Settings
      </Button>
    </div>
  );
}

function HoldDefaultsTab({ settings }: { settings: AppSettings }) {
  const qc = useQueryClient();
  const update = useUpdateSettings({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        toast.success("Settings saved");
      },
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const msg = (err as { message?: string })?.message;
        const detail = status ? `(${status})` : msg ? `(${msg})` : "";
        toast.error(`Failed to save settings${detail ? " " + detail : ""}`);
      },
    },
  });

  const [holdDays, setHoldDays] = useState(String(settings.holdDefaults.holdDays ?? 7));
  const [expiringSoonDays, setExpiringSoonDays] = useState(String(settings.holdDefaults.expiringSoonDays ?? 2));
  const [scheduledRefreshEnabled, setScheduledRefreshEnabled] = useState(!!settings.refresh.scheduledRefreshEnabled);
  const [reachInterval, setReachInterval] = useState(String(settings.refresh.reachIntervalMinutes ?? 10));
  const [ampInterval, setAmpInterval] = useState(String(settings.refresh.ampIntervalMinutes ?? 60));
  const [hideGetIn, setHideGetIn] = useState(!!settings.hideGetIn);

  useEffect(() => {
    setHoldDays(String(settings.holdDefaults.holdDays ?? 7));
    setExpiringSoonDays(String(settings.holdDefaults.expiringSoonDays ?? 2));
    setScheduledRefreshEnabled(!!settings.refresh.scheduledRefreshEnabled);
    setReachInterval(String(settings.refresh.reachIntervalMinutes ?? 10));
    setAmpInterval(String(settings.refresh.ampIntervalMinutes ?? 60));
    setHideGetIn(!!settings.hideGetIn);
  }, [
    settings.holdDefaults.holdDays,
    settings.holdDefaults.expiringSoonDays,
    settings.refresh.scheduledRefreshEnabled,
    settings.refresh.reachIntervalMinutes,
    settings.refresh.ampIntervalMinutes,
    settings.hideGetIn,
  ]);

  const save = () => {
    update.mutate({
      data: {
        holdDefaults: {
          holdDays: parseInt(holdDays) || 7,
          expiringSoonDays: parseInt(expiringSoonDays) || 2,
        },
        refresh: {
          scheduledRefreshEnabled,
          reachIntervalMinutes: parseInt(reachInterval) || 10,
          ampIntervalMinutes: parseInt(ampInterval) || 60,
        },
        hideGetIn,
      },
    });
  };

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">Admin Hold Defaults</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Hold Duration (days)</Label>
            <Input className="h-8 text-xs" value={holdDays} onChange={(e) => setHoldDays(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Expiring-Soon Window (days)</Label>
            <Input className="h-8 text-xs" value={expiringSoonDays} onChange={(e) => setExpiringSoonDays(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">Buyback Display</h3>
        <div className="flex items-center justify-between p-3 border border-border rounded bg-panel">
          <div>
            <div className="text-sm">Hide Margin / Get-In column</div>
            <div className="text-[11px] text-muted-foreground">
              Hides the Margin / Get-In column on buy-back events. Each listing&rsquo;s FVE
              intent (Buy Back / Sell) is set from the status picker instead, or in bulk via the
              selection bar.
            </div>
          </div>
          <Switch checked={hideGetIn} onCheckedChange={setHideGetIn} />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">Scheduled Refresh</h3>
        <div className="flex items-center justify-between p-3 border border-border rounded bg-panel">
          <div>
            <div className="text-sm">Enable scheduled refresh</div>
            <div className="text-[11px] text-muted-foreground">Automatically pull inventory on an interval</div>
          </div>
          <Switch checked={scheduledRefreshEnabled} onCheckedChange={setScheduledRefreshEnabled} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Reach Interval (min)</Label>
            <Input className="h-8 text-xs" value={reachInterval} onChange={(e) => setReachInterval(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">AMP Interval (min)</Label>
            <Input className="h-8 text-xs" value={ampInterval} onChange={(e) => setAmpInterval(e.target.value)} />
          </div>
        </div>
        <ScheduledRefreshStatus
          enabled={!!settings.refresh.scheduledRefreshEnabled}
          reachIntervalMinutes={settings.refresh.reachIntervalMinutes ?? 10}
          ampIntervalMinutes={settings.refresh.ampIntervalMinutes ?? 60}
        />
      </div>

      <Button size="sm" onClick={save} disabled={update.isPending}>
        {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
        Save Settings
      </Button>
    </div>
  );
}

type NotationKey = keyof NotationConfig;

const NOTATION_DEFAULTS: Record<NotationKey, NotationRule> = {
  holdPlaced: { enabled: true, template: "— Hold placed {days}d, expires {expires} ({date})" },
  holdExtended: { enabled: true, template: "— Hold extended {days}d, expires {expires} ({date})" },
  holdReleased: { enabled: true, template: "— Hold released ({date})" },
  cannotList: { enabled: true, template: "— Cannot List: {reason} ({date})" },
  moveToReach: { enabled: true, template: "— Moved to Sell via Reach on {date} by {user}" },
  moveToSkybox: { enabled: true, template: "— Moved to Sell via Skybox on {date} by {user}" },
  markedFve: { enabled: true, template: "— Marked FVE ({date})" },
  recommendedPriceSet: { enabled: true, template: "— Recommended price: {price} ({date})" },
  recommendedPriceAcked: { enabled: true, template: "— Listed at recommended {price} ({date})" },
  listingConfirmed: { enabled: true, template: "— Listed {qty}x at {price} ({date})" },
  saleConfirmed: { enabled: true, template: "— Sale confirmed: {qty}x at {price} ({date})" },
  soldOut: { enabled: true, template: "— Sold out ({date})" },
  bankAssigned: { enabled: true, template: "— Bank ...{bankLast4} assigned ({date})" },
  phoneGood: { enabled: true, template: "— Phone verified good ({date})" },
  phoneBad: { enabled: true, template: "— Phone verification failed ({date})" },
};

const NOTATION_META: Record<NotationKey, { label: string; description: string; vars: string[] }> = {
  holdPlaced: { label: "Hold Placed", description: "When an admin hold is first placed on a listing.", vars: ["date", "days", "expires"] },
  holdExtended: { label: "Hold Extended", description: "When an existing hold's expiry is pushed out.", vars: ["date", "days", "expires"] },
  holdReleased: { label: "Hold Released", description: "When a hold is released (manually or on move to marketplace).", vars: ["date"] },
  cannotList: { label: "Cannot List", description: "When a listing is marked as not listable, with a reason.", vars: ["date", "reason"] },
  moveToReach: { label: "Moved to Sell via Reach", description: "When a listing is moved to the Reach marketplace.", vars: ["date", "user"] },
  moveToSkybox: { label: "Moved to Sell via Skybox", description: "When a listing is moved to the Skybox marketplace.", vars: ["date", "user"] },
  markedFve: { label: "Marked FVE", description: "When a listing is marked as listed on FVE.", vars: ["date"] },
  recommendedPriceSet: { label: "Recommended Price Set", description: "When an operator sets a recommended price.", vars: ["date", "price"] },
  recommendedPriceAcked: { label: "Recommended Price Acknowledged", description: "When a listing-confirmation email confirms the recommended price.", vars: ["date", "price"] },
  listingConfirmed: { label: "Listing Confirmed", description: "When a listing-confirmation email is matched.", vars: ["date", "qty", "price"] },
  saleConfirmed: { label: "Sale Confirmed", description: "When a sale-confirmation email is matched to a listing.", vars: ["date", "qty", "price"] },
  soldOut: { label: "Sold Out", description: "When a listing drops out of Reach's available feed.", vars: ["date"] },
  bankAssigned: { label: "Bank Assigned", description: "When a bank slot is assigned to a vendor email.", vars: ["date", "user", "bankLast4"] },
  phoneGood: { label: "Phone Verified Good", description: "When an account's phone health flips to good.", vars: ["date", "user", "phone"] },
  phoneBad: { label: "Phone Verification Failed", description: "When an account's phone health flips to bad.", vars: ["date", "user", "phone"] },
};

/** Sample values used to render the live preview of each notation template. */
const NOTATION_SAMPLE_VARS: Record<string, string> = (() => {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const expires = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  return {
    date,
    days: "7",
    expires,
    reason: "venue restriction",
    user: "j.smith",
    price: "£120",
    qty: "4",
    bankLast4: "1234",
    phone: "+44 7700 900123",
  };
})();

/**
 * Replace every `{key}` token with its sample value, mirroring the server's
 * renderNotation: unknown tokens collapse to empty so no raw placeholders leak.
 */
function renderNotationPreview(template: string): string {
  return template.replace(/\{(\w+)\}/g, (_m, key: string) =>
    NOTATION_SAMPLE_VARS[key] != null ? NOTATION_SAMPLE_VARS[key] : "",
  );
}

const NOTATION_GROUPS: { title: string; keys: NotationKey[] }[] = [
  { title: "Hold", keys: ["holdPlaced", "holdExtended", "holdReleased"] },
  {
    title: "Listing & Sales",
    keys: [
      "cannotList",
      "moveToReach",
      "moveToSkybox",
      "markedFve",
      "recommendedPriceSet",
      "recommendedPriceAcked",
      "listingConfirmed",
      "saleConfirmed",
      "soldOut",
    ],
  },
  { title: "Phone & Banking", keys: ["bankAssigned", "phoneGood", "phoneBad"] },
];

function NotationTab({ settings }: { settings: AppSettings }) {
  const qc = useQueryClient();
  const update = useUpdateSettings({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        toast.success("Notation rules saved");
      },
      onError: () => toast.error("Failed to save notation rules"),
    },
  });

  const [config, setConfig] = useState<Record<NotationKey, NotationRule>>(() => {
    const out = {} as Record<NotationKey, NotationRule>;
    for (const key of Object.keys(NOTATION_DEFAULTS) as NotationKey[]) {
      out[key] = { ...(settings.notationConfig?.[key] ?? NOTATION_DEFAULTS[key]) };
    }
    return out;
  });

  const inputRefs = useRef<Partial<Record<NotationKey, HTMLInputElement | null>>>({});
  const [focusedKey, setFocusedKey] = useState<NotationKey | null>(null);

  const setRule = (key: NotationKey, patch: Partial<NotationRule>) =>
    setConfig((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  /** Insert a {var} token at the caret position of the rule's input, or append. */
  const insertVar = (key: NotationKey, v: string) => {
    const token = `{${v}}`;
    const el = inputRefs.current[key];
    const current = config[key].template;
    if (el && document.activeElement === el) {
      const start = el.selectionStart ?? current.length;
      const end = el.selectionEnd ?? current.length;
      const next = current.slice(0, start) + token + current.slice(end);
      setRule(key, { template: next });
      requestAnimationFrame(() => {
        el.focus();
        const caret = start + token.length;
        el.setSelectionRange(caret, caret);
      });
    } else {
      setRule(key, { template: current + token });
    }
  };

  const save = () => update.mutate({ data: { notationConfig: config } });

  const isDirty = (Object.keys(NOTATION_DEFAULTS) as NotationKey[]).some((key) => {
    const saved = settings.notationConfig?.[key] ?? NOTATION_DEFAULTS[key];
    const draft = config[key];
    return draft.enabled !== saved.enabled || draft.template !== saved.template;
  });

  return (
    <TooltipProvider delayDuration={200}>
      <div className="max-w-5xl space-y-4">
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
            Notation Rules
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Auto-append a note to each listing's Custom Notes when an action fires. Use
            variables like <code className="text-foreground">{"{date}"}</code> in the
            template — hover a rule name for details, click a chip to insert a variable.
          </p>
        </div>

        <div className="border-t border-border">
          {NOTATION_GROUPS.map((group) => (
            <Fragment key={group.title}>
              <div className="pt-3 pb-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide border-b border-border">
                {group.title}
              </div>
              {group.keys.map((key) => {
                const rule = config[key];
                const meta = NOTATION_META[key];
                const isDefault = rule.template === NOTATION_DEFAULTS[key].template;
                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 py-1.5 border-b border-border/50"
                  >
                    <Switch
                      className="shrink-0"
                      checked={rule.enabled}
                      onCheckedChange={(v) => setRule(key, { enabled: v })}
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={cn(
                            "w-56 shrink-0 whitespace-nowrap text-xs font-medium cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2",
                            !rule.enabled && "text-muted-foreground",
                          )}
                        >
                          {meta.label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px]">
                        {meta.description}
                      </TooltipContent>
                    </Tooltip>
                    <div className="flex-1 min-w-0 max-w-[30rem]">
                      <Input
                        type="text"
                        ref={(el) => {
                          inputRefs.current[key] = el;
                        }}
                        className="h-7 w-full text-xs font-mono px-2"
                        value={rule.template}
                        disabled={!rule.enabled}
                        onFocus={() => setFocusedKey(key)}
                        onBlur={() =>
                          setFocusedKey((prev) => (prev === key ? null : prev))
                        }
                        onChange={(e) => setRule(key, { template: e.target.value })}
                      />
                      {focusedKey === key && rule.template.trim() && (
                        <div className="mt-1 text-[11px] text-muted-foreground/70 italic truncate">
                          {renderNotationPreview(rule.template)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-nowrap gap-1 shrink-0">
                      {meta.vars.map((v) => (
                        <button
                          key={v}
                          type="button"
                          tabIndex={rule.enabled ? undefined : -1}
                          disabled={!rule.enabled}
                          onClick={() => insertVar(key, v)}
                          className="text-[10px] font-mono px-1 py-0.5 rounded border border-border bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-40"
                        >
                          {`{${v}}`}
                        </button>
                      ))}
                    </div>
                    <div className="w-6 shrink-0 flex justify-center">
                      {!isDefault && (
                        <button
                          type="button"
                          title="Reset to default"
                          aria-label="Reset to default"
                          onClick={() =>
                            setRule(key, { template: NOTATION_DEFAULTS[key].template })
                          }
                          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>

        {isDirty && <div className="h-14" />}

        {isDirty && (
          <div className="sticky bottom-0 -mx-1 px-1">
            <div className="flex items-center justify-between gap-3 rounded border border-border bg-panel/95 backdrop-blur px-3 py-2 shadow-lg">
              <span className="text-xs text-muted-foreground">You have unsaved changes</span>
              <Button size="sm" onClick={save} disabled={update.isPending}>
                {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Save Notation Rules
              </Button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

function formatRelativeTime(date: Date): string {
  const diffMs = date.getTime() - Date.now();
  const past = diffMs <= 0;
  const mins = Math.round(Math.abs(diffMs) / 60_000);
  let label: string;
  if (mins < 1) label = "less than a minute";
  else if (mins < 60) label = `${mins} min`;
  else {
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    label = rem ? `${hrs}h ${rem}m` : `${hrs}h`;
  }
  if (mins < 1) return past ? "just now" : "any moment";
  return past ? `${label} ago` : `in ${label}`;
}

function formatAbsoluteTime(date: Date): string {
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function RefreshStatusBadge({ status }: { status: string }) {
  const map: Record<string, { className: string; icon: typeof CheckCircle2 }> = {
    success: { className: "border-green-500/30 text-green-500 bg-green-500/10", icon: CheckCircle2 },
    error: { className: "border-red-500/30 text-red-400 bg-red-500/10", icon: XCircle },
    skipped: { className: "border-amber-500/30 text-amber-500 bg-amber-500/10", icon: MinusCircle },
    running: { className: "border-blue-500/30 text-blue-400 bg-blue-500/10", icon: Loader2 },
  };
  const cfg = map[status] ?? { className: "border-border text-muted-foreground", icon: MinusCircle };
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${cfg.className}`}>
      <Icon className={`h-3 w-3 ${status === "running" ? "animate-spin" : ""}`} />
      {status}
    </Badge>
  );
}

function RefreshRunRow({
  label,
  run,
  intervalMinutes,
  enabled,
}: {
  label: string;
  run: RefreshRun | null;
  intervalMinutes: number;
  enabled: boolean;
}) {
  const lastRaw = run?.finishedAt ?? run?.startedAt ?? null;
  const lastAt = lastRaw ? new Date(lastRaw) : null;
  const nextDue =
    enabled && lastAt
      ? new Date(lastAt.getTime() + intervalMinutes * 60_000)
      : null;

  return (
    <div className="flex items-start justify-between p-3 border border-border rounded bg-panel gap-4">
      <div className="space-y-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{label}</span>
          {run ? <RefreshStatusBadge status={run.status} /> : (
            <span className="text-[11px] text-muted-foreground">No automatic run yet</span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {lastAt ? (
            <>Last run {formatRelativeTime(lastAt)} · {formatAbsoluteTime(lastAt)}</>
          ) : (
            <>Runs every {intervalMinutes} min when scheduled refresh is on</>
          )}
        </div>
        {run?.status === "error" && run.error && (
          <div className="text-[11px] text-red-400 break-words" title={run.error}>
            {run.error}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        {!enabled ? (
          <span className="text-[11px] text-muted-foreground">Scheduling off</span>
        ) : nextDue ? (
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-1 text-xs text-foreground">
              <Clock className="h-3 w-3 text-muted-foreground" />
              Next {formatRelativeTime(nextDue)}
            </div>
            <span className="text-[11px] text-muted-foreground">{formatAbsoluteTime(nextDue)}</span>
          </div>
        ) : (
          <span className="text-[11px] text-muted-foreground">Next within {intervalMinutes} min</span>
        )}
      </div>
    </div>
  );
}

function ScheduledRefreshStatus({
  enabled,
  reachIntervalMinutes,
  ampIntervalMinutes,
}: {
  enabled: boolean;
  reachIntervalMinutes: number;
  ampIntervalMinutes: number;
}) {
  const { data: runs, isLoading } = useListRefreshRuns(
    { limit: 50 },
    {
      query: {
        queryKey: getListRefreshRunsQueryKey({ limit: 50 }),
        refetchInterval: 30_000,
      },
    },
  );

  // Automatic runs are created by the scheduler with no actor email; per-event
  // and manual refreshes carry the operator's email, so exclude those here.
  const latest = (type: "reach" | "amp"): RefreshRun | null =>
    (runs ?? []).find((r) => r.type === type && !r.actorEmail) ?? null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-muted-foreground">Automatic Run Status</Label>
        {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
      <RefreshRunRow
        label="Reach"
        run={latest("reach")}
        intervalMinutes={reachIntervalMinutes}
        enabled={enabled}
      />
      <RefreshRunRow
        label="AMP"
        run={latest("amp")}
        intervalMinutes={ampIntervalMinutes}
        enabled={enabled}
      />
    </div>
  );
}

function PhoneTab({ settings }: { settings: AppSettings }) {
  const qc = useQueryClient();
  const { data: phoneTypes, isLoading } = useListPhoneTypes();
  const updateSettings = useUpdateSettings({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        toast.success("Thresholds saved");
      },
      onError: () => toast.error("Failed to save thresholds"),
    },
  });
  const updateType = useUpdatePhoneType({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListPhoneTypesQueryKey() });
      },
      onError: () => toast.error("Failed to update phone type"),
    },
  });

  const [recencyDays, setRecencyDays] = useState(
    String(settings.phone?.recencyDays ?? 30),
  );
  const [recheckDelay, setRecheckDelay] = useState(
    String(settings.phone?.recheckDelayMinutes ?? 5),
  );
  // Max email-verification age (days) used to gate buyback account eligibility.
  const [verifyAgeDays, setVerifyAgeDays] = useState(
    String(settings.buyback?.forwardingAgeDays ?? 14),
  );

  const saveThresholds = () => {
    updateSettings.mutate({
      data: {
        phone: {
          recencyDays: parseInt(recencyDays) || 30,
          recheckDelayMinutes: parseInt(recheckDelay) || 5,
        },
        buyback: {
          forwardingAgeDays: parseInt(verifyAgeDays) || 14,
        },
      },
    });
  };

  const backfillAuditParams = {
    action: "account.backfill_phone_tags",
    limit: 1,
  } as const;
  const { data: backfillAudit } = useListAuditLog(backfillAuditParams);
  const lastBackfill = backfillAudit?.items?.[0];

  const backfillTags = useBackfillPhoneTags({
    mutation: {
      onSuccess: (res) => {
        toast.success(
          `Pushed phone tags to Reach for ${res.processed} account${res.processed === 1 ? "" : "s"}`,
        );
        qc.invalidateQueries({
          queryKey: getListAuditLogQueryKey(backfillAuditParams),
        });
      },
      onError: () => toast.error("Failed to push phone tags to Reach"),
    },
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
          Health Thresholds
        </h3>
        {!settings.phone?.twilioConfigured && (
          <div className="p-3 border border-amber-500/30 bg-amber-500/10 rounded text-xs text-amber-500">
            Twilio is not configured — test SMS sends will fail until TWILIO_SID,
            TWILIO_SECRET, and TWILIO_FROM_NUMBER are set.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">
              Good-if-SMS-within (days)
            </Label>
            <Input
              className="h-8 text-xs"
              value={recencyDays}
              onChange={(e) => setRecencyDays(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">
              Recheck delay after test (min)
            </Label>
            <Input
              className="h-8 text-xs"
              value={recheckDelay}
              onChange={(e) => setRecheckDelay(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">
              Max email verification age (days)
            </Label>
            <Input
              className="h-8 text-xs"
              value={verifyAgeDays}
              onChange={(e) => setVerifyAgeDays(e.target.value)}
            />
            <p className="text-[10px] text-muted-foreground">
              Buyback accounts whose forwarding email was last verified within
              this window are eligible; older or unverified accounts are excluded.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={saveThresholds} disabled={updateSettings.isPending}>
          {updateSettings.isPending && (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
          )}
          Save Thresholds
        </Button>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
          Reach Phone Status Tags
        </h3>
        <p className="text-xs text-muted-foreground">
          Re-sync the Reach "Phone Status" tag for every account with a known
          good or bad phone health. Use this to seed or repair tags on demand —
          accounts with an unknown status are left untouched.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={() => backfillTags.mutate()}
          disabled={backfillTags.isPending}
        >
          {backfillTags.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
          ) : (
            <Upload className="h-3.5 w-3.5 mr-1 text-blue-400" />
          )}
          Push phone tags to Reach
        </Button>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {lastBackfill?.createdAt ? (
            <span>
              Last pushed {formatAbsoluteTime(new Date(lastBackfill.createdAt))}
              {typeof (lastBackfill.after as { processed?: number } | null)
                ?.processed === "number"
                ? ` — ${(lastBackfill.after as { processed: number }).processed} account${(lastBackfill.after as { processed: number }).processed === 1 ? "" : "s"}`
                : ""}
              {lastBackfill.actorEmail ? ` by ${lastBackfill.actorEmail}` : ""}
            </span>
          ) : (
            <span>Never pushed yet</span>
          )}
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
          Phone Types
        </h3>
        <p className="text-xs text-muted-foreground">
          Automated types are included in the bulk "Test automated phones" run.
          Manual types are tested one at a time from the account.
        </p>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          <div className="border border-border rounded overflow-x-auto custom-scrollbar">
            <table className="w-full text-xs min-w-[560px]">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Phone Type</th>
                  <th className="text-right py-2 px-3 font-medium">Accounts</th>
                  <th className="text-left py-2 px-3 font-medium">Mode</th>
                  <th className="text-left py-2 px-3 font-medium">Automated</th>
                </tr>
              </thead>
              <tbody>
                {(phoneTypes ?? []).map((pt: PhoneTypeConfig) => (
                  <tr key={pt.phoneType} className="border-t border-border hover:bg-muted/40">
                    <td className="py-1.5 px-3 font-mono">{pt.phoneType}</td>
                    <td className="py-1.5 px-3 text-right font-mono">{pt.accountCount ?? 0}</td>
                    <td className="py-1.5 px-3">
                      <Badge
                        variant="outline"
                        className={
                          pt.mode === "automated"
                            ? "border-green-500/30 text-green-500 bg-green-500/10"
                            : "border-border text-muted-foreground"
                        }
                      >
                        {pt.mode}
                      </Badge>
                    </td>
                    <td className="py-1.5 px-3">
                      <Switch
                        checked={pt.mode === "automated"}
                        onCheckedChange={(v) =>
                          updateType.mutate({
                            data: {
                              phoneType: pt.phoneType,
                              mode: v ? "automated" : "manual",
                            },
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
                {phoneTypes && phoneTypes.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-6 text-muted-foreground">
                      No phone types found yet. Run an AMP refresh first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BankingSourcesTab() {
  const qc = useQueryClient();
  const { data: sources, isLoading } = useListBankingSources();
  const invalidate = () => qc.invalidateQueries({ queryKey: getListBankingSourcesQueryKey() });
  const create = useCreateBankingSource({
    mutation: {
      onSuccess: () => {
        invalidate();
        setForm({ name: "", bank: "", accountType: "" });
        toast.success("Banking source added");
      },
      onError: () => toast.error("Failed to add source"),
    },
  });
  const update = useUpdateBankingSource({
    mutation: { onSuccess: invalidate, onError: () => toast.error("Failed to update source") },
  });
  const [form, setForm] = useState({ name: "", bank: "", accountType: "" });

  const submit = () => {
    if (!form.name.trim()) {
      toast.error("Source name is required");
      return;
    }
    create.mutate({
      data: {
        name: form.name.trim(),
        bank: form.bank.trim() || undefined,
        accountType: form.accountType.trim() || undefined,
      },
    });
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <p className="text-xs text-muted-foreground">
        Configure the banking sources operators can pick from when adding a bank account
        (e.g. Wise, Revolut, Mercury checking/savings).
      </p>
      <div className="border border-border rounded bg-panel p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Add Source</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Name</Label>
            <Input className="h-8 w-48 text-xs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mercury Checking" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Bank</Label>
            <Input className="h-8 w-40 text-xs" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="Mercury" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Account Type</Label>
            <Input className="h-8 w-36 text-xs" value={form.accountType} onChange={(e) => setForm({ ...form, accountType: e.target.value })} placeholder="checking" />
          </div>
          <Button size="sm" className="h-8" onClick={submit} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            <span className="ml-1">Add</span>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <div className="border border-border rounded overflow-x-auto custom-scrollbar">
          <table className="w-full text-xs min-w-[560px]">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Enabled</th>
                <th className="text-left py-2 px-3 font-medium">Name</th>
                <th className="text-left py-2 px-3 font-medium">Bank</th>
                <th className="text-left py-2 px-3 font-medium">Account Type</th>
                <th className="text-right py-2 px-3 font-medium">Sort</th>
              </tr>
            </thead>
            <tbody>
              {(sources ?? []).map((s: BankingSource) => (
                <SourceRow key={s.id} source={s} onUpdate={(data) => update.mutate({ id: s.id, data })} />
              ))}
              {sources && sources.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No banking sources configured.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SourceRow({
  source,
  onUpdate,
}: {
  source: BankingSource;
  onUpdate: (data: { name?: string; bank?: string | null; accountType?: string | null; enabled?: boolean; sortOrder?: number }) => void;
}) {
  const [name, setName] = useState(source.name);
  const [bank, setBank] = useState(source.bank ?? "");
  const [accountType, setAccountType] = useState(source.accountType ?? "");
  const [sortOrder, setSortOrder] = useState(String(source.sortOrder));

  return (
    <tr className="border-t border-border hover:bg-muted/40">
      <td className="py-1.5 px-3">
        <Switch checked={source.enabled} onCheckedChange={(v) => onUpdate({ enabled: v })} />
      </td>
      <td className="py-1.5 px-3">
        <Input
          className="h-7 text-xs w-44"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => { if (name.trim() && name !== source.name) onUpdate({ name: name.trim() }); }}
        />
      </td>
      <td className="py-1.5 px-3">
        <Input
          className="h-7 text-xs w-36"
          value={bank}
          onChange={(e) => setBank(e.target.value)}
          onBlur={() => { if (bank !== (source.bank ?? "")) onUpdate({ bank: bank.trim() || null }); }}
        />
      </td>
      <td className="py-1.5 px-3">
        <Input
          className="h-7 text-xs w-32"
          value={accountType}
          onChange={(e) => setAccountType(e.target.value)}
          onBlur={() => { if (accountType !== (source.accountType ?? "")) onUpdate({ accountType: accountType.trim() || null }); }}
        />
      </td>
      <td className="py-1.5 px-3 text-right">
        <Input
          className="h-7 text-xs w-14 text-right"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          onBlur={() => { const n = parseInt(sortOrder); if (!Number.isNaN(n) && n !== source.sortOrder) onUpdate({ sortOrder: n }); }}
        />
      </td>
    </tr>
  );
}

function PullerCombobox({
  value,
  pullers,
  onChange,
}: {
  value: number | null;
  pullers: Puller[];
  onChange: (pullerId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = value != null ? pullers.find((p) => p.id === value) : undefined;
  const label = selected
    ? `${selected.name}${selected.tag ? ` (${selected.tag})` : ""}`
    : "Unassigned";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Linked puller"
          className="h-7 w-44 text-xs bg-transparent inline-flex items-center justify-between gap-1 rounded-md border border-input px-3 py-1 text-foreground shadow-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search pullers..." className="h-8 text-xs" />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
              No pullers found.
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="Unassigned"
                className="text-xs"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-3.5 w-3.5",
                    value == null ? "opacity-100" : "opacity-0",
                  )}
                />
                <span>Unassigned</span>
              </CommandItem>
              {pullers.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.name}${p.tag ? ` ${p.tag}` : ""}`}
                  className="text-xs"
                  onSelect={() => {
                    onChange(p.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      value === p.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">
                    {p.name}
                    {p.tag ? ` (${p.tag})` : ""}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const { data: users, isLoading } = useListUsers();
  const { data: pullers } = useListPullers();
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const update = useUpdateUser({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListUsersQueryKey() });
        toast.success("User updated");
      },
      onError: () => toast.error("Failed to update user"),
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  const filteredUsers = (users ?? []).filter((u: AppUser) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (unlinkedOnly && u.pullerId) return false;
    return true;
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Role</span>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="operator">Operator</SelectItem>
              <SelectItem value="pricing">Pricing</SelectItem>
              <SelectItem value="puller">Puller</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox checked={unlinkedOnly} onCheckedChange={(v) => setUnlinkedOnly(v === true)} />
          <span className="text-muted-foreground">Unlinked only</span>
        </label>
        <span className="text-muted-foreground ml-auto">{filteredUsers.length} of {(users ?? []).length}</span>
      </div>
      <div className="border border-border rounded overflow-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            <th className="text-left py-2 px-3 font-medium">User</th>
            <th className="text-left py-2 px-3 font-medium">Role</th>
            <th className="text-left py-2 px-3 font-medium">Linked Puller</th>
            <th className="text-left py-2 px-3 font-medium">Last Login</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((u: AppUser) => (
            <tr key={u.id} className="border-t border-border hover:bg-muted/40">
              <td className="py-1.5 px-3">
                <div className="font-medium">{u.name || u.email}</div>
                <div className="text-muted-foreground">{u.email}</div>
              </td>
              <td className="py-1.5 px-3">
                <Select value={u.role} onValueChange={(v) => update.mutate({ id: u.id, data: { role: v as AppUser["role"] } })}>
                  <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="pricing">Pricing</SelectItem>
                    <SelectItem value="puller">Puller</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="py-1.5 px-3">
                <PullerCombobox
                  value={u.pullerId ?? null}
                  pullers={pullers ?? []}
                  onChange={(pullerId) => update.mutate({ id: u.id, data: { pullerId } })}
                />
              </td>
              <td className="py-1.5 px-3 text-muted-foreground">
                {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "Never"}
              </td>
            </tr>
          ))}
          {filteredUsers.length === 0 && (
            <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">{(users ?? []).length === 0 ? "No users yet." : "No users match the current filters."}</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function slackErrorMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 503) return "Slack is not configured. Add a Slack integration to enable alerts.";
  return "Could not reach Slack. Check the Slack integration and try again.";
}

function AlertsTab() {
  return (
    <Tabs defaultValue="channels" className="w-full">
      <TabsList className="bg-transparent justify-start h-auto flex-wrap gap-2 p-0 mb-4">
        <TabsTrigger value="channels" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Channels &amp; Users</TabsTrigger>
        <TabsTrigger value="mapping" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Mapping</TabsTrigger>
        <TabsTrigger value="config" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Configuration</TabsTrigger>
      </TabsList>
      <TabsContent value="channels" className="m-0">
        <SlackBrowserTab />
      </TabsContent>
      <TabsContent value="mapping" className="m-0">
        <SlackMappingTab />
      </TabsContent>
      <TabsContent value="config" className="m-0">
        <SlackConfigTab />
      </TabsContent>
    </Tabs>
  );
}

function SlackBrowserTab() {
  const [channelSearch, setChannelSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const channels = useListSlackChannels();
  const users = useListSlackUsers();

  const filteredChannels = (channels.data ?? []).filter((c: SlackChannel) =>
    c.name.toLowerCase().includes(channelSearch.toLowerCase()),
  );
  const filteredUsers = (users.data ?? []).filter((u: SlackUser) => {
    const q = userSearch.toLowerCase();
    return (
      u.name.toLowerCase().includes(q) ||
      (u.realName ?? "").toLowerCase().includes(q) ||
      (u.displayName ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Channels</Label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{filteredChannels.length}</span>
            <button
              type="button"
              onClick={() => { channels.refetch(); users.refetch(); }}
              disabled={channels.isFetching}
              aria-label="Refresh channels from Slack"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <RotateCcw className={cn("h-3 w-3", channels.isFetching && "animate-spin")} />
              Refresh
            </button>
          </div>
        </div>
        <Input
          value={channelSearch}
          onChange={(e) => setChannelSearch(e.target.value)}
          placeholder="Search channels..."
          className="h-7 text-xs"
        />
        <div className="border border-border rounded overflow-auto max-h-96">
          {channels.isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : channels.isError ? (
            <div className="py-6 px-3 text-center text-xs text-muted-foreground">{slackErrorMessage(channels.error)}</div>
          ) : filteredChannels.length === 0 ? (
            <div className="py-6 px-3 text-center text-xs text-muted-foreground">No channels found.</div>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {filteredChannels.map((c: SlackChannel) => (
                  <tr key={c.id} className="border-t border-border first:border-t-0 hover:bg-muted/40">
                    <td className="py-1.5 px-3">
                      <span className="text-muted-foreground">#</span> {c.name}
                      {c.isPrivate && <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">private</Badge>}
                    </td>
                    <td className="py-1.5 px-3 text-right text-muted-foreground">
                      {c.isMember ? "joined" : ""}{c.numMembers != null ? ` ${c.numMembers}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Users</Label>
          <span className="text-xs text-muted-foreground">{filteredUsers.length}</span>
        </div>
        <Input
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Search users..."
          className="h-7 text-xs"
        />
        <div className="border border-border rounded overflow-auto max-h-96">
          {users.isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : users.isError ? (
            <div className="py-6 px-3 text-center text-xs text-muted-foreground">{slackErrorMessage(users.error)}</div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-6 px-3 text-center text-xs text-muted-foreground">No users found.</div>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {filteredUsers.map((u: SlackUser) => (
                  <tr key={u.id} className="border-t border-border first:border-t-0 hover:bg-muted/40">
                    <td className="py-1.5 px-3">
                      <div className="font-medium">{u.realName || u.displayName || u.name}</div>
                      <div className="text-muted-foreground">@{u.name}</div>
                    </td>
                    <td className="py-1.5 px-3 text-right">
                      {u.isBot && <Badge variant="outline" className="text-[10px] px-1 py-0">bot</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function SlackUserCombobox({
  value,
  users,
  onChange,
}: {
  value: string | null;
  users: SlackUser[];
  onChange: (slackId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = value != null ? users.find((u) => u.id === value) : undefined;
  const label = selected
    ? selected.realName || selected.displayName || selected.name
    : "Unmapped";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Slack user"
          className="h-7 w-52 text-xs bg-transparent inline-flex items-center justify-between gap-1 rounded-md border border-input px-3 py-1 text-foreground shadow-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search Slack users..." className="h-8 text-xs" />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">No users found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="Unmapped"
                className="text-xs"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check className={cn("mr-2 h-3.5 w-3.5", value == null ? "opacity-100" : "opacity-0")} />
                <span>Unmapped</span>
              </CommandItem>
              {users.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${u.realName ?? ""} ${u.displayName ?? ""} ${u.name}`}
                  className="text-xs"
                  onSelect={() => {
                    onChange(u.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-3.5 w-3.5", value === u.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">
                    {u.realName || u.displayName || u.name}
                    <span className="text-muted-foreground"> @{u.name}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function confidenceBadge(c: SlackAutoMapSuggestion["confidence"]) {
  if (c === "high") return <Badge className="text-[10px] px-1 py-0 bg-emerald-600 hover:bg-emerald-600">high</Badge>;
  if (c === "medium") return <Badge className="text-[10px] px-1 py-0 bg-amber-500 hover:bg-amber-500">medium</Badge>;
  if (c === "low") return <Badge variant="outline" className="text-[10px] px-1 py-0">low</Badge>;
  return null;
}

function SlackMappingTab() {
  const qc = useQueryClient();
  const users = useListUsers();
  const pullers = useListPullers();
  const slackUsers = useListSlackUsers();
  const [overrides, setOverrides] = useState<Record<number, string | null>>({});
  const [suggestions, setSuggestions] = useState<Record<number, SlackAutoMapSuggestion>>({});

  const autoMap = useAutoMapSlackUsers({
    mutation: {
      onSuccess: (data: SlackAutoMapSuggestion[]) => {
        const nextOverrides: Record<number, string | null> = {};
        const nextSuggestions: Record<number, SlackAutoMapSuggestion> = {};
        let matched = 0;
        for (const s of data) {
          nextSuggestions[s.pullerId] = s;
          if (s.suggestedSlackId) {
            nextOverrides[s.pullerId] = s.suggestedSlackId;
            matched++;
          }
        }
        setOverrides((prev) => ({ ...prev, ...nextOverrides }));
        setSuggestions(nextSuggestions);
        toast.success(`Auto-mapped ${matched} puller${matched === 1 ? "" : "s"}`);
      },
      onError: (err) => toast.error(slackErrorMessage(err)),
    },
  });

  const save = useSaveSlackMappings({
    mutation: {
      onSuccess: (res) => {
        qc.invalidateQueries({ queryKey: getListSlackAlertConfigsQueryKey() });
        setOverrides({});
        setSuggestions({});
        toast.success(`Saved ${res.saved} mapping${res.saved === 1 ? "" : "s"}`);
      },
      onError: () => toast.error("Failed to save mappings"),
    },
  });

  const pullerList = pullers.data ?? [];
  const slackUserList = slackUsers.data ?? [];
  const effectiveSlackId = (pullerId: number): string | null => {
    if (pullerId in overrides) return overrides[pullerId];
    return pullerList.find((p) => p.id === pullerId)?.slackId ?? null;
  };

  const usersWithPuller = (users.data ?? []).filter((u: AppUser) => u.pullerId != null);
  const hasChanges = Object.keys(overrides).length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs rounded-full"
          disabled={autoMap.isPending || slackUsers.isError}
          onClick={() => autoMap.mutate()}
        >
          {autoMap.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Link2 className="h-3.5 w-3.5 mr-1 text-blue-400" />}
          Auto-Map by Name
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={!hasChanges || save.isPending}
          onClick={() => {
            const mappings = Object.entries(overrides).map(([pullerId, slackId]) => ({
              pullerId: Number(pullerId),
              slackId: slackId,
            }));
            save.mutate({ data: { mappings } });
          }}
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
          Save Mappings
        </Button>
        {slackUsers.isError && (
          <span className="text-xs text-muted-foreground ml-1">{slackErrorMessage(slackUsers.error)}</span>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{usersWithPuller.length} linked users</span>
      </div>
      <div className="border border-border rounded overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left py-2 px-3 font-medium">User</th>
              <th className="text-left py-2 px-3 font-medium">Linked Puller</th>
              <th className="text-left py-2 px-3 font-medium">Slack User</th>
              <th className="text-left py-2 px-3 font-medium">Match</th>
            </tr>
          </thead>
          <tbody>
            {users.isLoading ? (
              <tr><td colSpan={4} className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary inline" /></td></tr>
            ) : usersWithPuller.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No users are linked to a puller yet.</td></tr>
            ) : (
              usersWithPuller.map((u: AppUser) => {
                const puller = pullerList.find((p) => p.id === u.pullerId);
                const sug = u.pullerId != null ? suggestions[u.pullerId] : undefined;
                return (
                  <tr key={u.id} className="border-t border-border hover:bg-muted/40">
                    <td className="py-1.5 px-3">
                      <div className="font-medium">{u.name || u.email}</div>
                      <div className="text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="py-1.5 px-3">
                      {puller ? `${puller.name}${puller.tag ? ` (${puller.tag})` : ""}` : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-1.5 px-3">
                      <SlackUserCombobox
                        value={u.pullerId != null ? effectiveSlackId(u.pullerId) : null}
                        users={slackUserList}
                        onChange={(slackId) => {
                          if (u.pullerId != null) {
                            setOverrides((prev) => ({ ...prev, [u.pullerId as number]: slackId }));
                          }
                        }}
                      />
                    </td>
                    <td className="py-1.5 px-3">
                      {sug ? (
                        <span className="inline-flex items-center gap-1">
                          {confidenceBadge(sug.confidence)}
                          {sug.confidence !== "none" && <span className="text-muted-foreground">{sug.score}%</span>}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SlackTargetCombobox({
  targetType,
  targetId,
  channels,
  users,
  onChange,
}: {
  targetType: "channel" | "user";
  targetId: string | null;
  channels: SlackChannel[];
  users: SlackUser[];
  onChange: (targetType: "channel" | "user", targetId: string | null) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshFromSlack = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        qc.invalidateQueries({ queryKey: getListSlackChannelsQueryKey() }),
        qc.invalidateQueries({ queryKey: getListSlackUsersQueryKey() }),
      ]);
    } finally {
      setRefreshing(false);
    }
  };
  let label = "Select destination";
  if (targetId) {
    if (targetType === "channel") {
      const c = channels.find((x) => x.id === targetId);
      if (c) label = `# ${c.name}`;
    } else {
      const u = users.find((x) => x.id === targetId);
      if (u) label = `@ ${u.realName || u.displayName || u.name}`;
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Alert destination"
          className="h-7 w-56 text-xs bg-transparent inline-flex items-center justify-between gap-1 rounded-md border border-input px-3 py-1 text-foreground shadow-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className={cn("truncate", !targetId && "text-muted-foreground")}>{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search destinations..." className="h-8 text-xs" />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">Nothing found.</CommandEmpty>
            <CommandGroup heading="Channels">
              {channels.map((c) => (
                <CommandItem
                  key={`c-${c.id}`}
                  value={`channel ${c.name}`}
                  className="text-xs"
                  onSelect={() => {
                    onChange("channel", c.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-3.5 w-3.5", targetType === "channel" && targetId === c.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate"># {c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Users">
              {users.map((u) => (
                <CommandItem
                  key={`u-${u.id}`}
                  value={`user ${u.realName ?? ""} ${u.displayName ?? ""} ${u.name}`}
                  className="text-xs"
                  onSelect={() => {
                    onChange("user", u.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-3.5 w-3.5", targetType === "user" && targetId === u.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">@ {u.realName || u.displayName || u.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={refreshFromSlack}
              disabled={refreshing}
              className="flex w-full items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <RotateCcw className={cn("h-3 w-3", refreshing && "animate-spin")} />
              {refreshing ? "Refreshing…" : "Refresh from Slack"}
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SlackConfigTab() {
  const qc = useQueryClient();
  const configs = useListSlackAlertConfigs();
  const channels = useListSlackChannels();
  const slackUsers = useListSlackUsers();
  const activePullers = useListPullers({ activeOnly: true });

  const update = useUpdateSlackAlertConfig({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListSlackAlertConfigsQueryKey() });
      },
      onError: () => toast.error("Failed to update alert config"),
    },
  });

  const test = useTestSlackAlert({
    mutation: {
      onSuccess: (res) => {
        if (res.ok) toast.success("Test alert sent");
        else toast.error(res.error ? `Test failed: ${res.error}` : "Test alert failed");
      },
      onError: () => toast.error("Failed to send test alert"),
    },
  });

  const channelList = channels.data ?? [];
  const slackUserList = slackUsers.data ?? [];
  const allConfigs = configs.data ?? [];

  const activeIds = new Set((activePullers.data ?? []).map((p) => p.id));
  const rows = activeIds.size > 0 ? allConfigs.filter((c) => activeIds.has(c.pullerId)) : allConfigs;

  const slackUnavailable = channels.isError || slackUsers.isError;

  return (
    <div className="space-y-2">
      {slackUnavailable && (
        <div className="text-xs text-muted-foreground">{slackErrorMessage(channels.error ?? slackUsers.error)}</div>
      )}
      <div className="border border-border rounded overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Puller</th>
              <th className="text-left py-2 px-3 font-medium">Daily Summary</th>
              <th className="text-left py-2 px-3 font-medium">Destination</th>
              <th className="text-left py-2 px-3 font-medium">Hour (UTC)</th>
              <th className="text-left py-2 px-3 font-medium">Test</th>
            </tr>
          </thead>
          <tbody>
            {configs.isLoading ? (
              <tr><td colSpan={5} className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No active pullers to configure.</td></tr>
            ) : (
              rows.map((c: SlackAlertConfig) => (
                <tr key={c.pullerId} className="border-t border-border hover:bg-muted/40">
                  <td className="py-1.5 px-3">
                    <div className="font-medium">{c.pullerName}</div>
                    {c.pullerTag && <div className="text-muted-foreground">{c.pullerTag}</div>}
                  </td>
                  <td className="py-1.5 px-3">
                    <Switch
                      checked={c.enabled}
                      onCheckedChange={(v) =>
                        update.mutate({
                          pullerId: c.pullerId,
                          data: {
                            enabled: v,
                            alertType: c.alertType,
                            targetType: c.targetType,
                            targetId: c.targetId ?? null,
                            scheduleHour: c.scheduleHour,
                          },
                        })
                      }
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <SlackTargetCombobox
                      targetType={c.targetType}
                      targetId={c.targetId ?? null}
                      channels={channelList}
                      users={slackUserList}
                      onChange={(targetType, targetId) =>
                        update.mutate({
                          pullerId: c.pullerId,
                          data: {
                            enabled: c.enabled,
                            alertType: c.alertType,
                            targetType,
                            targetId,
                            scheduleHour: c.scheduleHour,
                          },
                        })
                      }
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <Select
                      value={String(c.scheduleHour)}
                      onValueChange={(v) =>
                        update.mutate({
                          pullerId: c.pullerId,
                          data: {
                            enabled: c.enabled,
                            alertType: c.alertType,
                            targetType: c.targetType,
                            targetId: c.targetId ?? null,
                            scheduleHour: Number(v),
                          },
                        })
                      }
                    >
                      <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, h) => (
                          <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-1.5 px-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs rounded-full"
                      disabled={test.isPending || !c.targetId}
                      onClick={() => test.mutate({ pullerId: c.pullerId })}
                    >
                      {test.isPending && test.variables?.pullerId === c.pullerId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                      ) : (
                        <Zap className="h-3.5 w-3.5 mr-1 text-amber-400" />
                      )}
                      Test Fire
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <HoldAlertsSection channels={channelList} users={slackUserList} />
    </div>
  );
}

function HoldAlertsSection({
  channels,
  users,
}: {
  channels: SlackChannel[];
  users: SlackUser[];
}) {
  const qc = useQueryClient();
  const configs = useListHoldAlertConfigs();

  const update = useUpdateHoldAlertConfig({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListHoldAlertConfigsQueryKey() });
      },
      onError: () => toast.error("Failed to update hold alert"),
    },
  });

  const test = useTestHoldAlert({
    mutation: {
      onSuccess: (res) => {
        if (res.ok) toast.success("Test alert sent");
        else toast.error(res.error ? `Test failed: ${res.error}` : "Test alert failed");
      },
      onError: () => toast.error("Failed to send test alert"),
    },
  });

  const rows = configs.data ?? [];

  return (
    <div className="space-y-2 pt-4">
      <div>
        <h3 className="text-sm font-semibold">Hold Alerts</h3>
        <p className="text-xs text-muted-foreground">
          Notify a Slack channel or user when a hold is set or released. Fires
          after the hold is verified in Reach. "Notify owner" also DMs the puller
          who owns the listing's account (pullers can opt out under
          Notifications).
        </p>
      </div>
      <div className="border border-border rounded overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Alert</th>
              <th className="text-left py-2 px-3 font-medium">Enabled</th>
              <th className="text-left py-2 px-3 font-medium">Destination</th>
              <th className="text-left py-2 px-3 font-medium">Notify owner</th>
              <th className="text-left py-2 px-3 font-medium">Bulk</th>
              <th className="text-left py-2 px-3 font-medium">Test</th>
            </tr>
          </thead>
          <tbody>
            {configs.isLoading ? (
              <tr><td colSpan={6} className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary inline" /></td></tr>
            ) : (
              rows.map((c: HoldAlertConfig) => (
                <tr key={c.alertType} className="border-t border-border hover:bg-muted/40">
                  <td className="py-1.5 px-3 font-medium">{c.label}</td>
                  <td className="py-1.5 px-3">
                    <Switch
                      checked={c.enabled}
                      onCheckedChange={(v) =>
                        update.mutate({
                          alertType: c.alertType,
                          data: { enabled: v },
                        })
                      }
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <SlackTargetCombobox
                      targetType={c.targetType}
                      targetId={c.targetId ?? null}
                      channels={channels}
                      users={users}
                      onChange={(targetType, targetId) =>
                        update.mutate({
                          alertType: c.alertType,
                          data: { targetType, targetId },
                        })
                      }
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <Switch
                      checked={c.notifyOwner}
                      onCheckedChange={(v) =>
                        update.mutate({
                          alertType: c.alertType,
                          data: { notifyOwner: v },
                        })
                      }
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <Select
                      value={c.bulkMode}
                      onValueChange={(v) =>
                        update.mutate({
                          alertType: c.alertType,
                          data: { bulkMode: v as "summary" | "per_listing" },
                        })
                      }
                    >
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="summary">Summary</SelectItem>
                        <SelectItem value="per_listing">Per listing</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-1.5 px-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs rounded-full"
                      disabled={test.isPending || !c.targetId}
                      onClick={() => test.mutate({ alertType: c.alertType })}
                    >
                      {test.isPending && test.variables?.alertType === c.alertType ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                      ) : (
                        <Zap className="h-3.5 w-3.5 mr-1 text-amber-400" />
                      )}
                      Test Fire
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
